// Camera controls matching antimatter15's implementation
export class CameraControls {
    constructor(canvas, viewer, initialViewMatrix = null) {
        this.canvas = canvas;
        this.viewer = viewer;
        this.down = 0;
        this.startX = 0;
        this.startY = 0;
        
        // Touch-specific state
        this.touches = new Map(); // Track multiple touches
        this.lastTouchDistance = 0; // For pinch-to-zoom
        this.lastTouchCenter = { x: 0, y: 0 }; // For two-finger pan
        this.touchMode = 'none'; // 'rotate', 'zoom', 'pan'
        this.lastTapTime = 0; // For double-tap detection
        
        // Bind event handlers
        this.onMouseDown = this.onMouseDown.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.onMouseUp = this.onMouseUp.bind(this);
        this.onWheel = this.onWheel.bind(this);
        this.onContextMenu = this.onContextMenu.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        
        // Bind touch event handlers
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        
        // Add event listeners
        canvas.addEventListener('mousedown', this.onMouseDown);
        canvas.addEventListener('mousemove', this.onMouseMove);
        canvas.addEventListener('mouseup', this.onMouseUp);
        canvas.addEventListener('wheel', this.onWheel, { passive: false });
        canvas.addEventListener('contextmenu', this.onContextMenu);
        window.addEventListener('keydown', this.onKeyDown);
        window.addEventListener('keyup', this.onKeyUp);
        
        // Add touch event listeners
        canvas.addEventListener('touchstart', this.onTouchStart, { passive: false });
        canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });
        canvas.addEventListener('touchend', this.onTouchEnd, { passive: false });
        
        // Active keys tracking
        this.activeKeys = new Set();
        
        // Initialize view matrix with provided matrix or antimatter15's default
        if (initialViewMatrix) {
            this.viewMatrix = new Float32Array(initialViewMatrix);
        } else {
            this.viewMatrix = new Float32Array([
                0.99, 0.01, -0.14, 0,
                0.02, 0.99, 0.12, 0,
                0.14, -0.12, 0.98, 0,
                -0.09, -0.26, 0.2, 1
            ]);
        }
        
        // Store camera state as position and quaternion internally
        // Extract initial position from view matrix
        const inv = this.invert4(this.viewMatrix);
        this.position = [inv[12], inv[13], inv[14]];
        const rotMatrix = this.getRotationMatrix();
        this.quaternion = this.quaternionFromMatrix(rotMatrix);
        
        // Previous state for change detection - initialize as undefined
        // This ensures the first camera update will always emit an event
        this.prevPosition = undefined;
        this.prevQuaternion = undefined;
        
        // Set initial camera - always emit initial state
        // This ensures UI components get the initial camera position
        setTimeout(() => {
            this.updateRenderer();
        }, 0);
    }
    
    // Set view matrix from external source (e.g., for camera presets)
    setViewMatrix(viewMatrix) {
        this.viewMatrix.set(viewMatrix);
        this.updateRenderer();
    }
    
    // Set camera from position and quaternion
    setCameraFromPositionQuaternion(position, quaternion) {
        // Store position and quaternion directly
        this.position = [...position];
        this.quaternion = { ...quaternion };
        
        // Rebuild view matrix from position and quaternion
        this.updateViewMatrix();
        this.updateRenderer();
    }
    
    // Extract camera position and quaternion from internal state
    getCameraState() {
        // Return position and quaternion directly - no Euler conversion
        return { 
            position: [...this.position],
            quaternion: { ...this.quaternion }
        };
    }
    
    // Get rotation matrix from view matrix
    getRotationMatrix() {
        const inv = this.invert4(this.viewMatrix);
        return [
            inv[0], inv[1], inv[2], 0,
            inv[4], inv[5], inv[6], 0,
            inv[8], inv[9], inv[10], 0,
            0, 0, 0, 1
        ];
    }
    
    // Update view matrix from stored position and quaternion
    updateViewMatrix() {
        // Convert quaternion to rotation matrix
        const rotMatrix = this.quaternionToMatrix(this.quaternion);
        
        // Create camera matrix (inverse of view matrix)
        const camMatrix = new Float32Array(16);
        camMatrix[0] = rotMatrix[0];
        camMatrix[1] = rotMatrix[1];
        camMatrix[2] = rotMatrix[2];
        camMatrix[3] = 0;
        camMatrix[4] = rotMatrix[4];
        camMatrix[5] = rotMatrix[5];
        camMatrix[6] = rotMatrix[6];
        camMatrix[7] = 0;
        camMatrix[8] = rotMatrix[8];
        camMatrix[9] = rotMatrix[9];
        camMatrix[10] = rotMatrix[10];
        camMatrix[11] = 0;
        camMatrix[12] = this.position[0];
        camMatrix[13] = this.position[1];
        camMatrix[14] = this.position[2];
        camMatrix[15] = 1;
        
        // Invert to get view matrix
        this.viewMatrix = this.invert4(camMatrix);
        
        // Debug: Check if round-trip is correct (commented out for production)
        /*
        const testInv = this.invert4(this.viewMatrix);
        const testRot = this.getRotationMatrix();
        const testQuat = this.quaternionFromMatrix(testRot);
        
        // Only log if there's significant difference
        const quatDiff = Math.abs(testQuat.w - this.quaternion.w) + 
                        Math.abs(testQuat.x - this.quaternion.x) +
                        Math.abs(testQuat.y - this.quaternion.y) +
                        Math.abs(testQuat.z - this.quaternion.z);
        if (quatDiff > 0.01) {
            console.warn('Quaternion round-trip error!');
            console.log('Original quaternion:', this.quaternion);
            console.log('Round-trip quaternion:', testQuat);
            console.log('Camera matrix:', Array.from(camMatrix));
            console.log('View matrix:', Array.from(this.viewMatrix));
        }
        */
    }
    
    onMouseDown(e) {
        e.preventDefault();
        this.startX = e.clientX;
        this.startY = e.clientY;
        this.down = e.ctrlKey || e.metaKey ? 2 : 1;
    }
    
    onMouseMove(e) {
        e.preventDefault();
        if (this.down === 1) {
            // Rotation using quaternions
            const dx = (5 * (e.clientX - this.startX)) / window.innerWidth;
            const dy = (5 * (e.clientY - this.startY)) / window.innerHeight;
            
            // Rotation around Y axis (yaw)
            const yawQuat = this.quaternionFromAxisAngle([0, 1, 0], dx);
            
            // Rotation around local X axis (pitch)
            const pitchAxis = this.rotateVectorByQuaternion([1, 0, 0], this.quaternion);
            const pitchQuat = this.quaternionFromAxisAngle(pitchAxis, -dy);
            
            // Apply rotations
            this.quaternion = this.quaternionMultiply(yawQuat, this.quaternion);
            this.quaternion = this.quaternionMultiply(pitchQuat, this.quaternion);
            this.quaternion = this.quaternionNormalize(this.quaternion);
            
            this.updateViewMatrix();
            this.updateRenderer();
            
            this.startX = e.clientX;
            this.startY = e.clientY;
        } else if (this.down === 2) {
            // Pan - move in camera space
            const dx = (-10 * (e.clientX - this.startX)) / window.innerWidth;
            const dz = (10 * (e.clientY - this.startY)) / window.innerHeight;
            
            // Transform movement by camera rotation
            const right = this.rotateVectorByQuaternion([1, 0, 0], this.quaternion);
            const forward = this.rotateVectorByQuaternion([0, 0, -1], this.quaternion);
            
            this.position[0] += right[0] * dx + forward[0] * dz;
            this.position[1] += right[1] * dx + forward[1] * dz;
            this.position[2] += right[2] * dx + forward[2] * dz;
            
            this.updateViewMatrix();
            this.updateRenderer();
            
            this.startX = e.clientX;
            this.startY = e.clientY;
        }
    }
    
    onMouseUp() {
        this.down = 0;
    }
    
    onWheel(e) {
        e.preventDefault();
        const lineHeight = 10;
        const scale = e.deltaMode === 1 ? lineHeight : e.deltaMode === 2 ? window.innerHeight : 1;
        
        if (e.shiftKey) {
            // Pan with wheel
            const right = this.rotateVectorByQuaternion([1, 0, 0], this.quaternion);
            const up = this.rotateVectorByQuaternion([0, 1, 0], this.quaternion);
            
            const dx = (e.deltaX * scale) / window.innerWidth;
            const dy = (e.deltaY * scale) / window.innerHeight;
            
            this.position[0] += right[0] * dx + up[0] * dy;
            this.position[1] += right[1] * dx + up[1] * dy;
            this.position[2] += right[2] * dx + up[2] * dy;
        } else if (e.ctrlKey || e.metaKey) {
            // Zoom
            const forward = this.rotateVectorByQuaternion([0, 0, -1], this.quaternion);
            const dz = (-10 * (e.deltaY * scale)) / window.innerHeight;
            
            this.position[0] += forward[0] * dz;
            this.position[1] += forward[1] * dz;
            this.position[2] += forward[2] * dz;
        } else {
            // Rotate around center
            const dx = -(e.deltaX * scale) / window.innerWidth;
            const dy = (e.deltaY * scale) / window.innerHeight;
            
            // Rotation around Y axis (yaw)
            const yawQuat = this.quaternionFromAxisAngle([0, 1, 0], dx);
            
            // Rotation around local X axis (pitch)
            const right = this.rotateVectorByQuaternion([1, 0, 0], this.quaternion);
            const pitchQuat = this.quaternionFromAxisAngle(right, dy);
            
            // Apply rotations
            this.quaternion = this.quaternionMultiply(yawQuat, this.quaternion);
            this.quaternion = this.quaternionMultiply(pitchQuat, this.quaternion);
            this.quaternion = this.quaternionNormalize(this.quaternion);
        }
        
        this.updateViewMatrix();
        this.updateRenderer();
    }
    
    onContextMenu(e) {
        e.preventDefault();
    }
    
    onKeyDown(e) {
        this.activeKeys.add(e.code);
        
        // Handle O key for control mode toggle
        if (e.code === 'KeyO' && this.viewer.renderer && this.viewer.renderer.toggleControlMode) {
            // This is handled by the renderer, but we need to reinitialize
            // For now, just emit the event
            return;
        }
    }
    
    onKeyUp(e) {
        this.activeKeys.delete(e.code);
    }
    
    // Update camera based on keyboard input
    updateFromKeyboard() {
        if (this.activeKeys.size === 0) return;
        
        let updated = false;
        
        // Movement and rotation speeds
        const moveSpeed = 0.1;
        const rotSpeed = 0.02;
        
        // Get camera directions for movement (world space)
        // The quaternion represents the view transform, so we need its inverse for world directions
        const invQuat = {
            x: -this.quaternion.x,
            y: -this.quaternion.y,
            z: -this.quaternion.z,
            w: this.quaternion.w
        };
        const forward = this.rotateVectorByQuaternion([0, 0, -1], invQuat);
        const right = this.rotateVectorByQuaternion([1, 0, 0], invQuat);
        const up = this.rotateVectorByQuaternion([0, 1, 0], invQuat);
        
        // Get camera directions for rotation (local space)
        // These use the original quaternion for local axis calculations
        const localForward = this.rotateVectorByQuaternion([0, 0, -1], this.quaternion);
        const localRight = this.rotateVectorByQuaternion([1, 0, 0], this.quaternion);
        
        // Movement (in view space, -Z is forward)
        if (this.activeKeys.has('KeyW')) {
            this.position[0] -= forward[0] * moveSpeed;
            this.position[1] -= forward[1] * moveSpeed;
            this.position[2] -= forward[2] * moveSpeed;
            updated = true;
        }
        if (this.activeKeys.has('KeyS')) {
            this.position[0] += forward[0] * moveSpeed;
            this.position[1] += forward[1] * moveSpeed;
            this.position[2] += forward[2] * moveSpeed;
            updated = true;
        }
        if (this.activeKeys.has('KeyA')) {
            this.position[0] -= right[0] * moveSpeed;
            this.position[1] -= right[1] * moveSpeed;
            this.position[2] -= right[2] * moveSpeed;
            updated = true;
        }
        if (this.activeKeys.has('KeyD')) {
            this.position[0] += right[0] * moveSpeed;
            this.position[1] += right[1] * moveSpeed;
            this.position[2] += right[2] * moveSpeed;
            updated = true;
        }
        
        // Q/E behavior based on control mode
        const isFreeCameraMode = this.viewer.renderer && this.viewer.renderer.controlMode === 'free';
        
        if (this.activeKeys.has('KeyQ')) {
            if (isFreeCameraMode) {
                // Free mode: Move down along camera's local up axis
                this.position[0] -= up[0] * moveSpeed;
                this.position[1] -= up[1] * moveSpeed;
                this.position[2] -= up[2] * moveSpeed;
            } else {
                // Orbit mode: Roll rotation (tilt left) - use local forward axis
                const rollQuat = this.quaternionFromAxisAngle(localForward, rotSpeed);
                this.quaternion = this.quaternionMultiply(rollQuat, this.quaternion);
            }
            updated = true;
        }
        if (this.activeKeys.has('KeyE')) {
            if (isFreeCameraMode) {
                // Free mode: Move up along camera's local up axis
                this.position[0] += up[0] * moveSpeed;
                this.position[1] += up[1] * moveSpeed;
                this.position[2] += up[2] * moveSpeed;
            } else {
                // Orbit mode: Roll rotation (tilt right) - use local forward axis
                const rollQuat = this.quaternionFromAxisAngle(localForward, -rotSpeed);
                this.quaternion = this.quaternionMultiply(rollQuat, this.quaternion);
            }
            updated = true;
        }
        
        // Arrow key rotation
        if (this.activeKeys.has('ArrowLeft')) {
            const yawQuat = this.quaternionFromAxisAngle([0, 1, 0], rotSpeed);
            this.quaternion = this.quaternionMultiply(yawQuat, this.quaternion);
            updated = true;
        }
        if (this.activeKeys.has('ArrowRight')) {
            const yawQuat = this.quaternionFromAxisAngle([0, 1, 0], -rotSpeed);
            this.quaternion = this.quaternionMultiply(yawQuat, this.quaternion);
            updated = true;
        }
        if (this.activeKeys.has('ArrowUp')) {
            // Use local right axis for pitch rotation
            const pitchQuat = this.quaternionFromAxisAngle(localRight, -rotSpeed);
            this.quaternion = this.quaternionMultiply(pitchQuat, this.quaternion);
            updated = true;
        }
        if (this.activeKeys.has('ArrowDown')) {
            // Use local right axis for pitch rotation
            const pitchQuat = this.quaternionFromAxisAngle(localRight, rotSpeed);
            this.quaternion = this.quaternionMultiply(pitchQuat, this.quaternion);
            updated = true;
        }
        
        if (updated) {
            this.quaternion = this.quaternionNormalize(this.quaternion);
            this.updateViewMatrix();
            this.updateRenderer();
        }
    }
    
    // Helper to check if camera state has changed
    hasCameraChanged(position, quaternion, tolerance = 0.0001) {
        // If no previous state, this is the first update - always emit
        if (!this.prevPosition || !this.prevQuaternion) {
            return true;
        }
        
        // Check position change
        for (let i = 0; i < 3; i++) {
            if (Math.abs(position[i] - this.prevPosition[i]) > tolerance) {
                return true;
            }
        }
        
        // Check quaternion change
        if (Math.abs(quaternion.w - this.prevQuaternion.w) > tolerance ||
            Math.abs(quaternion.x - this.prevQuaternion.x) > tolerance ||
            Math.abs(quaternion.y - this.prevQuaternion.y) > tolerance ||
            Math.abs(quaternion.z - this.prevQuaternion.z) > tolerance) {
            return true;
        }
        
        return false;
    }
    
    updateRenderer() {
        // Use the new direct view matrix setter
        if (this.viewer && this.viewer.setViewMatrix) {
            this.viewer.setViewMatrix(this.viewMatrix);
            
            // Don't emit events if the renderer is programmatically setting the camera
            if (this.viewer.renderer && this.viewer.renderer.isSettingCamera) {
                return;
            }
            
            // Extract and emit camera state for UI updates
            const state = this.getCameraState();
            
            // Only emit if camera has actually changed
            if (this.hasCameraChanged(state.position, state.quaternion)) {
                if (this.viewer.renderer && this.viewer.renderer.emit) {
                    this.viewer.renderer.emit('cameraChange', state.position, state.quaternion);
                }
                
                // Update previous state
                this.prevPosition = [...state.position];
                this.prevQuaternion = { ...state.quaternion };
            }
        } else {
        }
    }
    
    // Matrix operations
    invert4(mat) {
        const m = mat;
        const inv = new Float32Array(16);
        
        inv[0] = m[5] * m[10] * m[15] - m[5] * m[11] * m[14] - m[9] * m[6] * m[15] +
                 m[9] * m[7] * m[14] + m[13] * m[6] * m[11] - m[13] * m[7] * m[10];
        inv[4] = -m[4] * m[10] * m[15] + m[4] * m[11] * m[14] + m[8] * m[6] * m[15] -
                 m[8] * m[7] * m[14] - m[12] * m[6] * m[11] + m[12] * m[7] * m[10];
        inv[8] = m[4] * m[9] * m[15] - m[4] * m[11] * m[13] - m[8] * m[5] * m[15] +
                 m[8] * m[7] * m[13] + m[12] * m[5] * m[11] - m[12] * m[7] * m[9];
        inv[12] = -m[4] * m[9] * m[14] + m[4] * m[10] * m[13] + m[8] * m[5] * m[14] -
                  m[8] * m[6] * m[13] - m[12] * m[5] * m[10] + m[12] * m[6] * m[9];
        
        inv[1] = -m[1] * m[10] * m[15] + m[1] * m[11] * m[14] + m[9] * m[2] * m[15] -
                 m[9] * m[3] * m[14] - m[13] * m[2] * m[11] + m[13] * m[3] * m[10];
        inv[5] = m[0] * m[10] * m[15] - m[0] * m[11] * m[14] - m[8] * m[2] * m[15] +
                 m[8] * m[3] * m[14] + m[12] * m[2] * m[11] - m[12] * m[3] * m[10];
        inv[9] = -m[0] * m[9] * m[15] + m[0] * m[11] * m[13] + m[8] * m[1] * m[15] -
                 m[8] * m[3] * m[13] - m[12] * m[1] * m[11] + m[12] * m[3] * m[9];
        inv[13] = m[0] * m[9] * m[14] - m[0] * m[10] * m[13] - m[8] * m[1] * m[14] +
                  m[8] * m[2] * m[13] + m[12] * m[1] * m[10] - m[12] * m[2] * m[9];
        
        inv[2] = m[1] * m[6] * m[15] - m[1] * m[7] * m[14] - m[5] * m[2] * m[15] +
                 m[5] * m[3] * m[14] + m[13] * m[2] * m[7] - m[13] * m[3] * m[6];
        inv[6] = -m[0] * m[6] * m[15] + m[0] * m[7] * m[14] + m[4] * m[2] * m[15] -
                 m[4] * m[3] * m[14] - m[12] * m[2] * m[7] + m[12] * m[3] * m[6];
        inv[10] = m[0] * m[5] * m[15] - m[0] * m[7] * m[13] - m[4] * m[1] * m[15] +
                  m[4] * m[3] * m[13] + m[12] * m[1] * m[7] - m[12] * m[3] * m[5];
        inv[14] = -m[0] * m[5] * m[14] + m[0] * m[6] * m[13] + m[4] * m[1] * m[14] -
                  m[4] * m[2] * m[13] - m[12] * m[1] * m[6] + m[12] * m[2] * m[5];
        
        inv[3] = -m[1] * m[6] * m[11] + m[1] * m[7] * m[10] + m[5] * m[2] * m[11] -
                 m[5] * m[3] * m[10] - m[9] * m[2] * m[7] + m[9] * m[3] * m[6];
        inv[7] = m[0] * m[6] * m[11] - m[0] * m[7] * m[10] - m[4] * m[2] * m[11] +
                 m[4] * m[3] * m[10] + m[8] * m[2] * m[7] - m[8] * m[3] * m[6];
        inv[11] = -m[0] * m[5] * m[11] + m[0] * m[7] * m[9] + m[4] * m[1] * m[11] -
                  m[4] * m[3] * m[9] - m[8] * m[1] * m[7] + m[8] * m[3] * m[5];
        inv[15] = m[0] * m[5] * m[10] - m[0] * m[6] * m[9] - m[4] * m[1] * m[10] +
                  m[4] * m[2] * m[9] + m[8] * m[1] * m[6] - m[8] * m[2] * m[5];
        
        const det = m[0] * inv[0] + m[1] * inv[4] + m[2] * inv[8] + m[3] * inv[12];
        
        if (det === 0) return mat;
        
        const invDet = 1 / det;
        for (let i = 0; i < 16; i++) {
            inv[i] *= invDet;
        }
        
        return inv;
    }
    
    translate4(mat, x, y, z) {
        const result = new Float32Array(mat);
        result[12] = mat[0] * x + mat[4] * y + mat[8] * z + mat[12];
        result[13] = mat[1] * x + mat[5] * y + mat[9] * z + mat[13];
        result[14] = mat[2] * x + mat[6] * y + mat[10] * z + mat[14];
        result[15] = mat[3] * x + mat[7] * y + mat[11] * z + mat[15];
        return result;
    }
    
    rotate4(mat, rad, x, y, z) {
        let len = Math.hypot(x, y, z);
        if (len < 0.000001) return mat;
        
        len = 1 / len;
        x *= len;
        y *= len;
        z *= len;
        
        const s = Math.sin(rad);
        const c = Math.cos(rad);
        const t = 1 - c;
        
        const a00 = x * x * t + c;
        const a01 = y * x * t + z * s;
        const a02 = z * x * t - y * s;
        const a10 = x * y * t - z * s;
        const a11 = y * y * t + c;
        const a12 = z * y * t + x * s;
        const a20 = x * z * t + y * s;
        const a21 = y * z * t - x * s;
        const a22 = z * z * t + c;
        
        const result = new Float32Array(16);
        const m = mat;
        
        result[0] = m[0] * a00 + m[4] * a10 + m[8] * a20;
        result[1] = m[1] * a00 + m[5] * a10 + m[9] * a20;
        result[2] = m[2] * a00 + m[6] * a10 + m[10] * a20;
        result[3] = m[3] * a00 + m[7] * a10 + m[11] * a20;
        
        result[4] = m[0] * a01 + m[4] * a11 + m[8] * a21;
        result[5] = m[1] * a01 + m[5] * a11 + m[9] * a21;
        result[6] = m[2] * a01 + m[6] * a11 + m[10] * a21;
        result[7] = m[3] * a01 + m[7] * a11 + m[11] * a21;
        
        result[8] = m[0] * a02 + m[4] * a12 + m[8] * a22;
        result[9] = m[1] * a02 + m[5] * a12 + m[9] * a22;
        result[10] = m[2] * a02 + m[6] * a12 + m[10] * a22;
        result[11] = m[3] * a02 + m[7] * a12 + m[11] * a22;
        
        result[12] = m[12];
        result[13] = m[13];
        result[14] = m[14];
        result[15] = m[15];
        
        return result;
    }
    
    dispose() {
        this.canvas.removeEventListener('mousedown', this.onMouseDown);
        this.canvas.removeEventListener('mousemove', this.onMouseMove);
        this.canvas.removeEventListener('mouseup', this.onMouseUp);
        this.canvas.removeEventListener('wheel', this.onWheel);
        this.canvas.removeEventListener('contextmenu', this.onContextMenu);
        window.removeEventListener('keydown', this.onKeyDown);
        window.removeEventListener('keyup', this.onKeyUp);
        
        // Remove touch event listeners
        this.canvas.removeEventListener('touchstart', this.onTouchStart);
        this.canvas.removeEventListener('touchmove', this.onTouchMove);
        this.canvas.removeEventListener('touchend', this.onTouchEnd);
    }
    
    // Quaternion math utilities
    quaternionFromEuler(rx, ry, rz) {
        // YXZ order to match our current system
        const c1 = Math.cos(rx / 2);
        const c2 = Math.cos(ry / 2);
        const c3 = Math.cos(rz / 2);
        const s1 = Math.sin(rx / 2);
        const s2 = Math.sin(ry / 2);
        const s3 = Math.sin(rz / 2);
        
        return {
            x: s1 * c2 * c3 + c1 * s2 * s3,
            y: c1 * s2 * c3 - s1 * c2 * s3,
            z: c1 * c2 * s3 - s1 * s2 * c3,
            w: c1 * c2 * c3 + s1 * s2 * s3
        };
    }
    
    quaternionToEuler(q) {
        const { x, y, z, w } = q;
        
        // YXZ order
        const sinr_cosp = 2 * (w * x + y * z);
        const cosr_cosp = 1 - 2 * (x * x + y * y);
        const rx = Math.atan2(sinr_cosp, cosr_cosp);
        
        const sinp = 2 * (w * y - z * x);
        const ry = Math.abs(sinp) >= 1 ? Math.sign(sinp) * Math.PI / 2 : Math.asin(sinp);
        
        const siny_cosp = 2 * (w * z + x * y);
        const cosy_cosp = 1 - 2 * (y * y + z * z);
        const rz = Math.atan2(siny_cosp, cosy_cosp);
        
        return [rx, ry, rz];
    }
    
    quaternionFromAxisAngle(axis, angle) {
        const halfAngle = angle * 0.5;
        const s = Math.sin(halfAngle);
        
        // Normalize axis
        const length = Math.sqrt(axis[0] * axis[0] + axis[1] * axis[1] + axis[2] * axis[2]);
        const nx = axis[0] / length;
        const ny = axis[1] / length;
        const nz = axis[2] / length;
        
        return {
            x: nx * s,
            y: ny * s,
            z: nz * s,
            w: Math.cos(halfAngle)
        };
    }
    
    quaternionMultiply(a, b) {
        return {
            x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
            y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
            z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
            w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z
        };
    }
    
    quaternionNormalize(q) {
        const length = Math.sqrt(q.x * q.x + q.y * q.y + q.z * q.z + q.w * q.w);
        if (length === 0) {
            return { x: 0, y: 0, z: 0, w: 1 };
        }
        return {
            x: q.x / length,
            y: q.y / length,
            z: q.z / length,
            w: q.w / length
        };
    }
    
    quaternionToMatrix(q) {
        const { x, y, z, w } = q;
        
        const x2 = x + x;
        const y2 = y + y;
        const z2 = z + z;
        
        const xx = x * x2;
        const xy = x * y2;
        const xz = x * z2;
        const yy = y * y2;
        const yz = y * z2;
        const zz = z * z2;
        const wx = w * x2;
        const wy = w * y2;
        const wz = w * z2;
        
        return new Float32Array([
            1 - (yy + zz), xy - wz, xz + wy, 0,
            xy + wz, 1 - (xx + zz), yz - wx, 0,
            xz - wy, yz + wx, 1 - (xx + yy), 0,
            0, 0, 0, 1
        ]);
    }
    
    quaternionFromMatrix(m) {
        const trace = m[0] + m[5] + m[10];
        
        if (trace > 0) {
            const s = 0.5 / Math.sqrt(trace + 1.0);
            return {
                w: 0.25 / s,
                x: (m[9] - m[6]) * s,
                y: (m[2] - m[8]) * s,
                z: (m[4] - m[1]) * s
            };
        } else if (m[0] > m[5] && m[0] > m[10]) {
            const s = 2.0 * Math.sqrt(1.0 + m[0] - m[5] - m[10]);
            return {
                w: (m[9] - m[6]) / s,
                x: 0.25 * s,
                y: (m[1] + m[4]) / s,
                z: (m[2] + m[8]) / s
            };
        } else if (m[5] > m[10]) {
            const s = 2.0 * Math.sqrt(1.0 + m[5] - m[0] - m[10]);
            return {
                w: (m[2] - m[8]) / s,
                x: (m[1] + m[4]) / s,
                y: 0.25 * s,
                z: (m[6] + m[9]) / s
            };
        } else {
            const s = 2.0 * Math.sqrt(1.0 + m[10] - m[0] - m[5]);
            return {
                w: (m[4] - m[1]) / s,
                x: (m[2] + m[8]) / s,
                y: (m[6] + m[9]) / s,
                z: 0.25 * s
            };
        }
    }
    
    rotateVectorByQuaternion(v, q) {
        // Convert vector to quaternion
        const vq = { x: v[0], y: v[1], z: v[2], w: 0 };
        
        // q * v * q*
        const qConj = { x: -q.x, y: -q.y, z: -q.z, w: q.w };
        const temp = this.quaternionMultiply(q, vq);
        const result = this.quaternionMultiply(temp, qConj);
        
        return [result.x, result.y, result.z];
    }
    
    // Touch event handlers
    onTouchStart(e) {
        e.preventDefault();
        
        // Store current touches
        for (const touch of e.touches) {
            this.touches.set(touch.identifier, {
                x: touch.clientX,
                y: touch.clientY,
                startX: touch.clientX,
                startY: touch.clientY
            });
        }
        
        if (e.touches.length === 1) {
            // Single touch - check for double tap
            const currentTime = Date.now();
            if (currentTime - this.lastTapTime < 300) {
                // Double tap detected - reset camera
                this.resetCamera();
                this.lastTapTime = 0;
                return;
            }
            this.lastTapTime = currentTime;
            
            // Start rotation
            this.touchMode = 'rotate';
            const touch = e.touches[0];
            this.startX = touch.clientX;
            this.startY = touch.clientY;
        } else if (e.touches.length === 2) {
            // Two touches - determine if zoom or pan
            this.updateTwoFingerState(e.touches);
            this.touchMode = 'zoom'; // Default to zoom, may switch to pan based on movement
        }
    }
    
    onTouchMove(e) {
        e.preventDefault();
        
        // Update touch positions
        for (const touch of e.touches) {
            const touchData = this.touches.get(touch.identifier);
            if (touchData) {
                touchData.x = touch.clientX;
                touchData.y = touch.clientY;
            }
        }
        
        if (e.touches.length === 1 && this.touchMode === 'rotate') {
            // Single finger rotation
            const touch = e.touches[0];
            const dx = (5 * (touch.clientX - this.startX)) / window.innerWidth;
            const dy = (5 * (touch.clientY - this.startY)) / window.innerHeight;
            
            // Rotation around Y axis (yaw)
            const yawQuat = this.quaternionFromAxisAngle([0, 1, 0], dx);
            
            // Rotation around local X axis (pitch)
            const pitchAxis = this.rotateVectorByQuaternion([1, 0, 0], this.quaternion);
            const pitchQuat = this.quaternionFromAxisAngle(pitchAxis, -dy);
            
            // Apply rotations
            this.quaternion = this.quaternionMultiply(yawQuat, this.quaternion);
            this.quaternion = this.quaternionMultiply(pitchQuat, this.quaternion);
            this.quaternion = this.quaternionNormalize(this.quaternion);
            
            this.updateViewMatrix();
            this.updateRenderer();
            
            this.startX = touch.clientX;
            this.startY = touch.clientY;
        } else if (e.touches.length === 2) {
            // Two finger zoom or pan
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            
            // Calculate distance between touches
            const dx = touch2.clientX - touch1.clientX;
            const dy = touch2.clientY - touch1.clientY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // Calculate center of touches
            const centerX = (touch1.clientX + touch2.clientX) / 2;
            const centerY = (touch1.clientY + touch2.clientY) / 2;
            
            if (this.lastTouchDistance > 0) {
                // Pinch zoom
                const zoomScale = distance / this.lastTouchDistance;
                const zoomSpeed = (zoomScale - 1) * 2;
                
                const forward = this.rotateVectorByQuaternion([0, 0, -1], this.quaternion);
                this.position[0] += forward[0] * zoomSpeed;
                this.position[1] += forward[1] * zoomSpeed;
                this.position[2] += forward[2] * zoomSpeed;
                
                // Two finger pan
                const panDx = (centerX - this.lastTouchCenter.x) / window.innerWidth * 10;
                const panDy = (centerY - this.lastTouchCenter.y) / window.innerHeight * 10;
                
                const right = this.rotateVectorByQuaternion([1, 0, 0], this.quaternion);
                const up = this.rotateVectorByQuaternion([0, 1, 0], this.quaternion);
                
                this.position[0] -= right[0] * panDx + up[0] * panDy;
                this.position[1] -= right[1] * panDx + up[1] * panDy;
                this.position[2] -= right[2] * panDx + up[2] * panDy;
                
                this.updateViewMatrix();
                this.updateRenderer();
            }
            
            this.updateTwoFingerState(e.touches);
        }
    }
    
    onTouchEnd(e) {
        e.preventDefault();
        
        // Remove ended touches
        const remainingTouches = new Set();
        for (const touch of e.touches) {
            remainingTouches.add(touch.identifier);
        }
        
        for (const [id] of this.touches) {
            if (!remainingTouches.has(id)) {
                this.touches.delete(id);
            }
        }
        
        // Reset state if no touches remain
        if (e.touches.length === 0) {
            this.touchMode = 'none';
            this.lastTouchDistance = 0;
            this.lastTouchCenter = { x: 0, y: 0 };
        } else if (e.touches.length === 1) {
            // Switch back to rotation mode
            this.touchMode = 'rotate';
            const touch = e.touches[0];
            this.startX = touch.clientX;
            this.startY = touch.clientY;
            this.lastTouchDistance = 0;
        }
    }
    
    updateTwoFingerState(touches) {
        const touch1 = touches[0];
        const touch2 = touches[1];
        
        // Calculate distance
        const dx = touch2.clientX - touch1.clientX;
        const dy = touch2.clientY - touch1.clientY;
        this.lastTouchDistance = Math.sqrt(dx * dx + dy * dy);
        
        // Calculate center
        this.lastTouchCenter = {
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2
        };
    }
}